import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowUpRight,
  Loader2,
  MapPin,
  CalendarCheck,
  Home as HomeIcon,
  Thermometer,
  Flame,
  Droplet,
  Lightbulb,
  Square,
  Layers,
  AlertTriangle,
  ArrowLeft,
  Search as SearchIcon,
} from "lucide-react";
import Logo from "@/components/Logo";
import { epcSearch, epcCertificate } from "@/lib/api";
import { getBrowserId } from "@/lib/browserId";

const BAND_COLOR = {
  A: "bg-epc-a text-white",
  B: "bg-epc-b text-black",
  C: "bg-epc-c text-black",
  D: "bg-epc-d text-black",
  E: "bg-epc-e text-black",
  F: "bg-epc-f text-white",
  G: "bg-epc-g text-white",
};

export default function Check() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [postcode, setPostcode] = useState(params.get("postcode") || "");
  const [house, setHouse] = useState(params.get("house") || "");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [cert, setCert] = useState(null);
  const [certLoading, setCertLoading] = useState(false);

  async function runSearch(pc, hn) {
    const q = (pc ?? postcode).trim();
    const h = (hn ?? house).trim();
    if (!q) return;
    setLoading(true);
    setResults(null);
    setSelected(null);
    setCert(null);
    try {
      const data = await epcSearch(q, { house_number: h, source: "check", user_id: getBrowserId() });
      setResults(data);
      setParams(h ? { postcode: q, house: h } : { postcode: q });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Lookup failed");
    } finally {
      setLoading(false);
    }
  }

  // Auto-run on first load if postcode in URL
  useEffect(() => {
    const p = params.get("postcode");
    if (p && !results && !loading) runSearch(p, params.get("house") || "");
    // eslint-disable-next-line
  }, []);

  async function openCert(row) {
    setSelected(row);
    setCert(null);
    setCertLoading(true);
    try {
      const data = await epcCertificate(row.certificate_number, row.current_band);
      setCert({ ...data, current_band: row.current_band });
    } catch (e) {
      toast.error("Could not load certificate detail");
    } finally {
      setCertLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground font-sans" data-testid="check-page">
      <header className="sticky top-0 z-40 border-b border-border bg-white/70 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-6 h-16">
          <Link to="/" className="flex items-center gap-2">
            <Logo className="h-9 w-9" />
            <span className="font-heading text-base tracking-tight">chatepc<span className="text-epc-a">.co.uk</span></span>
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/" className="text-muted-foreground hover:text-foreground hidden md:inline-flex items-center gap-1">
              <ArrowLeft className="h-3.5 w-3.5" /> Home
            </Link>
            <Link to="/chat">
              <Button size="sm" className="rounded-none bg-epc-a hover:bg-epc-a/90 text-white">
                Ask ChatEPC <ArrowUpRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      <section className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">EPC Register lookup</div>
          <h1 className="mt-3 font-heading text-4xl md:text-5xl tracking-tight max-w-3xl">
            Check any UK property's <span className="inline-block px-2 bg-epc-a text-white">EPC</span>
          </h1>
          <p className="mt-4 text-muted-foreground max-w-2xl">
            Enter a postcode — and optionally a house number or name — to see Energy Performance Certificates on the official UK register. Covers England &amp; Wales — Scotland and Northern Ireland use separate registers.
          </p>

          <form
            onSubmit={(e) => { e.preventDefault(); runSearch(); }}
            className="mt-8 flex items-stretch max-w-2xl border border-foreground/20 bg-white"
          >
            <div className="pl-3 flex items-center text-muted-foreground">
              <HomeIcon className="h-4 w-4" />
            </div>
            <Input
              data-testid="check-house-input"
              value={house}
              onChange={(e) => setHouse(e.target.value)}
              placeholder="House no. / name (optional)"
              className="w-28 sm:w-56 border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 text-base"
              autoFocus
            />
            <div className="pl-3 flex items-center text-muted-foreground border-l border-border">
              <MapPin className="h-4 w-4" />
            </div>
            <Input
              data-testid="check-postcode-input"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              placeholder="Postcode e.g. LS1 4DL"
              className="flex-1 min-w-0 border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 text-base"
            />
            <Button
              type="submit"
              data-testid="check-search-btn"
              disabled={loading || !postcode.trim()}
              className="rounded-none bg-epc-a hover:bg-epc-a/90 text-white h-11 px-5"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>
                <SearchIcon className="h-4 w-4 mr-2" /> Check
              </>}
            </Button>
          </form>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-6 py-10">
        {loading && (
          <div className="py-16 grid place-items-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}

        {results && results.region === "scotland" && !loading && (
          <div className="border border-border p-6" data-testid="check-region-scotland">
            <div className="inline-flex items-center gap-2 bg-epc-c text-black px-2 py-1 text-xs uppercase tracking-widest">
              <AlertTriangle className="h-3.5 w-3.5" /> Scottish postcode
            </div>
            <h3 className="mt-4 font-heading text-xl">{results.postcode} is in Scotland</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Scotland has its own EPC register separate from England &amp; Wales. ChatEPC's live lookup covers England &amp; Wales only — please search the Scottish register for this address.
            </p>
            <a href="https://www.scottishepcregister.org.uk/" target="_blank" rel="noopener noreferrer" className="inline-block mt-4" data-testid="check-scotland-link">
              <Button className="rounded-none bg-epc-a hover:bg-epc-a/90 text-white">
                Open Scottish EPC Register <ArrowUpRight className="ml-1 h-4 w-4" />
              </Button>
            </a>
          </div>
        )}

        {results && results.region === "ni" && !loading && (
          <div className="border border-border p-6" data-testid="check-region-ni">
            <div className="inline-flex items-center gap-2 bg-epc-c text-black px-2 py-1 text-xs uppercase tracking-widest">
              <AlertTriangle className="h-3.5 w-3.5" /> Northern Ireland postcode
            </div>
            <h3 className="mt-4 font-heading text-xl">{results.postcode} is in Northern Ireland</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Northern Ireland EPCs are held on a separate register. ChatEPC's live lookup covers England &amp; Wales only — please search the NI register for this address.
            </p>
            <a href="https://www.epbni.com/" target="_blank" rel="noopener noreferrer" className="inline-block mt-4" data-testid="check-ni-link">
              <Button className="rounded-none bg-epc-a hover:bg-epc-a/90 text-white">
                Open NI EPB Register <ArrowUpRight className="ml-1 h-4 w-4" />
              </Button>
            </a>
          </div>
        )}

        {results && results.count === 0 && results.region === "ew" && !loading && (
          <div className="border border-border p-6 text-center" data-testid="check-no-results">
            <AlertTriangle className="h-6 w-6 text-epc-f mx-auto" />
            <h3 className="mt-3 font-heading text-xl">No certificates found</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              No EPCs on the England &amp; Wales register for <strong>{results.postcode}</strong>.
              The property may be in Scotland or Northern Ireland (separate registers), newly built,
              or the EPC may not yet be lodged.
            </p>
          </div>
        )}

        {results && results.count > 0 && !selected && results.region === "ew" && (
          <section data-testid="check-results">
            {results.house_number && results.house_matched === false && (
              <div className="mb-4 flex items-start gap-2 border border-epc-c bg-epc-c/10 px-4 py-3 text-sm" data-testid="check-house-no-match">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  We couldn't find an exact match for <strong>{results.house_number}</strong> at {results.postcode} — showing all certificates for this postcode instead.
                </span>
              </div>
            )}
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="font-heading text-xl tracking-tight">
                {results.count} certificate{results.count === 1 ? "" : "s"} for {results.house_matched ? `${results.house_number}, ` : ""}{results.postcode}
              </h2>
              <span className="text-xs text-muted-foreground">Click any row for the full certificate</span>
            </div>
            <ul className="border border-border divide-y divide-border">
              {results.results.map((r) => (
                <li key={r.certificate_number}>
                  <button
                    onClick={() => openCert(r)}
                    data-testid={`check-row-${r.certificate_number}`}
                    className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-neutral-50 transition-colors"
                  >
                    <BandBadge letter={r.current_band} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">
                        {[r.address_line_1, r.address_line_2, r.address_line_3].filter(Boolean).join(", ")}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {r.post_town} · {r.postcode} · registered {r.registration_date}
                        {r.expiry_date ? ` · valid until ${r.expiry_date}` : ""}
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {selected && (
          <section data-testid="check-detail">
            <button
              onClick={() => { setSelected(null); setCert(null); }}
              className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to results
            </button>
            {certLoading && (
              <div className="py-16 grid place-items-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            )}
            {cert && <CertificateCard cert={cert} selected={selected} navigate={navigate} />}
          </section>
        )}
      </main>

      <footer className="border-t border-border mt-12">
        <div className="max-w-5xl mx-auto px-6 py-6 text-xs text-muted-foreground">
          Data sourced from the UK gov.uk Open Data Communities EPC register. Covers England &amp; Wales only.
        </div>
      </footer>
    </div>
  );
}

function BandBadge({ letter }) {
  const l = (letter || "?").toUpperCase();
  const cls = BAND_COLOR[l] || "bg-neutral-200 text-black";
  return (
    <div className={`h-12 w-12 grid place-items-center font-heading font-extrabold text-2xl ${cls}`}>
      {l}
    </div>
  );
}

function Info({ icon, label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-start gap-2 py-2">
      <div className="text-muted-foreground mt-0.5">{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="text-sm">{value}</div>
      </div>
    </div>
  );
}

function CertificateCard({ cert, selected, navigate }) {
  const title = [selected.address_line_1, selected.address_line_2, selected.address_line_3]
    .filter(Boolean).join(", ");
  return (
    <article className="border border-border">
      <div className="flex items-center gap-4 p-4 bg-neutral-50 border-b border-border">
        <BandBadge letter={cert.current_band} />
        <div className="flex-1 min-w-0">
          <div className="font-heading text-lg tracking-tight">{title}</div>
          <div className="text-xs text-muted-foreground">
            {cert.post_town} · {cert.postcode} · UPRN {cert.uprn || "—"}
          </div>
        </div>
        <Link
          to={`/chat?prefill=${encodeURIComponent(
            `I'm looking at the EPC for ${title}, ${cert.postcode}. Current band ${cert.current_band || "unknown"}. What are the best ways to improve this property's rating?`
          )}`}
          data-testid="cert-ask-chatepc"
        >
          <Button className="rounded-none bg-epc-a hover:bg-epc-a/90 text-white hidden md:inline-flex">
            Ask ChatEPC about this <ArrowUpRight className="ml-1 h-4 w-4" />
          </Button>
        </Link>
      </div>

      <div className="grid md:grid-cols-2 gap-x-8 gap-y-0 px-4 py-3">
        <Info icon={<HomeIcon className="h-4 w-4" />} label="Property type" value={[cert.property_type, cert.built_form].filter(Boolean).join(" · ")} />
        <Info icon={<Square className="h-4 w-4" />} label="Total floor area" value={cert.total_floor_area_sqm ? `${cert.total_floor_area_sqm} m²` : null} />
        <Info icon={<Layers className="h-4 w-4" />} label="Construction age band" value={cert.construction_age_band} />
        <Info icon={<CalendarCheck className="h-4 w-4" />} label="Registered" value={cert.registration_date} />
        <Info icon={<CalendarCheck className="h-4 w-4" />} label="Expires" value={cert.expiry_date} />
        <Info icon={<CalendarCheck className="h-4 w-4" />} label="Inspection date" value={cert.inspection_date} />
        <Info icon={<Flame className="h-4 w-4" />} label="Main heating" value={cert.main_heating_description} />
        <Info icon={<Flame className="h-4 w-4" />} label="Secondary heating" value={cert.secondary_heating_description} />
        <Info icon={<Droplet className="h-4 w-4" />} label="Hot water" value={cert.hot_water_description} />
        <Info icon={<Thermometer className="h-4 w-4" />} label="Walls" value={cert.walls_description} />
        <Info icon={<Thermometer className="h-4 w-4" />} label="Roof" value={cert.roof_description} />
        <Info icon={<Thermometer className="h-4 w-4" />} label="Floor" value={cert.floor_description} />
        <Info icon={<Thermometer className="h-4 w-4" />} label="Windows" value={cert.windows_description} />
        <Info icon={<Lightbulb className="h-4 w-4" />} label="Lighting" value={cert.lighting_description} />
        <Info icon={<HomeIcon className="h-4 w-4" />} label="Assessment type" value={cert.assessment_type} />
        <Info icon={<HomeIcon className="h-4 w-4" />} label="Transaction" value={cert.transaction_type} />
      </div>

      <div className="p-4 border-t border-border md:hidden">
        <Link
          to={`/chat?prefill=${encodeURIComponent(
            `I'm looking at the EPC for ${title}, ${cert.postcode}. Current band ${cert.current_band || "unknown"}. What are the best ways to improve this property's rating?`
          )}`}
        >
          <Button className="w-full rounded-none bg-epc-a hover:bg-epc-a/90 text-white">
            Ask ChatEPC about this property <ArrowUpRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    </article>
  );
}
